<template>
  <div>
    <v-card
      :outlined="sidebarBreakpoint.andUp"
      :flat="sidebarBreakpoint.andDown"
      class="results-options-card"
    >
      <!-- SMALL DEVICES CLOSE -->
      <v-btn
        v-if="sidebarBreakpoint.andDown"
        rounded
        depressed
        outlined
        color="white"
        small
        class="mt-3"
        @click="handleClickClose"
      >
        <v-icon
          color="primary"
          class="mr-1"
        >
          mdi-close-circle
        </v-icon>
        <span class="primary--text text-decoration-underline">Close</span>
      </v-btn>

      <!-- TITLE -->
      <v-card-title
        class="py-3"
      >
        Adjust Cover Options
      </v-card-title>

      <v-card-text>
        <!-- EDIT/SUMMARY BTNS -->
        <v-row dense>
          <v-col>
            <v-btn
              block
              outlined
              rounded
              color="primary"
              @click.prevent="onClickEditQuote"
            >
              <v-icon
                small
                class="mr-1"
              >
                mdi-pencil
              </v-icon>
              <span class="caption font-weight-medium">Edit Quote</span>
            </v-btn>
          </v-col>
          <v-col>
            <v-btn
              block
              outlined
              rounded
              color="primary"
              @click="showQuoteDetails"
            >
              <span class="caption font-weight-medium">Quote Summary</span>
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="mt-3" />

        <!-- FILTERS -->
        <v-expansion-panels
          v-model="panelsShown"
          multiple
          accordion
          flat
        >
          <!-- PAYMENT TYPE -->
          <v-expansion-panel>
            <v-expansion-panel-header><strong>Payment Type</strong></v-expansion-panel-header>
            <v-expansion-panel-content v-scroll-to-viewport>
              <v-row dense>
                <v-col>
                  <v-btn
                    color="primary"
                    :outlined="!selectedFilters.isViewMonthly"
                    block
                    rounded
                    class="body-2"
                    @click="handleClickPaymentType(true)"
                  >
                    <v-icon
                      :size="20"
                      class="mr-1"
                    >
                      {{ selectedFilters.isViewMonthly ? '$radioOn' : '$radioOff' }}
                    </v-icon>
                    <span>Monthly</span>
                  </v-btn>
                </v-col>
                <v-col>
                  <v-btn
                    color="primary"
                    :outlined="selectedFilters.isViewMonthly"
                    block
                    rounded
                    class="body-2"
                    @click="handleClickPaymentType(false)"
                  >
                    <v-icon
                      :size="20"
                      class="mr-1"
                    >
                      {{ !selectedFilters.isViewMonthly ? '$radioOn' : '$radioOff' }}
                    </v-icon>
                    <span>Annually</span>
                  </v-btn>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
            <div
              v-show="!panelsShown.includes(0)"
              class="v-expansion-panel-content__wrap"
            >
              <p class="caption mb-0">
                {{ selectedFilters.isViewMonthly ? 'Monthly' : 'Annually' }}
              </p>
            </div>
            <v-divider />
          </v-expansion-panel>

          <!-- RACING COVER -->
          <v-expansion-panel>
            <v-expansion-panel-header><strong>Racing Cover</strong></v-expansion-panel-header>
            <v-expansion-panel-content v-scroll-to-viewport>
              <v-select
                v-model="bicycleRacingCover"
                :items="constants.YES_NO"
                outlined
                dense
                hide-details
                attach
                class="mt-2"
              />
            </v-expansion-panel-content>
            <div
              v-show="!panelsShown.includes(1)"
              class="v-expansion-panel-content__wrap"
            >
              <p class="caption mb-0">
                {{ getBooleanLabel(bicycleRacingCover) }}
              </p>
            </div>
            <v-divider />
          </v-expansion-panel>

          <!-- NON-COMPETITIVE EVENTS COVER -->
          <v-expansion-panel>
            <v-expansion-panel-header><strong>Non-competitive Events Cover</strong></v-expansion-panel-header>
            <v-expansion-panel-content v-scroll-to-viewport>
              <v-select
                v-model="bicycleNonCompetitiveCover"
                :items="constants.YES_NO"
                outlined
                dense
                hide-details
                attach
                class="mt-2"
              />
            </v-expansion-panel-content>
            <div
              v-show="!panelsShown.includes(2)"
              class="v-expansion-panel-content__wrap"
            >
              <p class="caption mb-0">
                {{ getBooleanLabel(bicycleNonCompetitiveCover) }}
              </p>
            </div>
            <v-divider />
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header><strong>Maximum Excess</strong></v-expansion-panel-header>
            <v-expansion-panel-content v-scroll-to-viewport>
              <v-select
                v-model="maxExcess"
                :items="options.maxExcess"
                outlined
                dense
                hide-details
                attach
                class="mt-2"
              />
            </v-expansion-panel-content>
            <div
              v-show="!panelsShown.includes(3)"
              class="v-expansion-panel-content__wrap"
            >
              <p class="caption mb-0">
                {{ maxExcessText }}
              </p>
            </div>
            <v-divider />
          </v-expansion-panel>

          <!-- OPTIONAL EXTRAS -->
          <v-expansion-panel>
            <v-expansion-panel-header><strong>Optional Extras</strong></v-expansion-panel-header>
            <v-expansion-panel-content v-scroll-to-viewport>
              <p class="body-2 mb-0">
                Additional extras to enhance your policy.
              </p>
              <a
                href="#"
                class="caption"
                @click.prevent="showOptionalExtras"
              >
                Learn more about optional extras
              </a>
              <v-checkbox
                v-for="(extra, index) in options.optionalExtrasOptions"
                :key="index"
                v-model="optionalExtras"
                :label="extra.label"
                :value="extra.value"
                hide-details
                dense
                class="mt-2 text-capitalize"
                @change="handleOptionalExtras($event, extra.value)"
              />
            </v-expansion-panel-content>
            <div
              v-show="!panelsShown.includes(4)"
              class="v-expansion-panel-content__wrap"
            >
              <p
                v-if="optionalExtras && optionalExtras.length > 0"
                class="caption mb-0"
              >
                <span
                  v-for="(extratitle, key) in optionalExtras"
                  :key="`${key}`"
                >
                  {{ getOptionalExtrasTitle(extratitle) }}<span
                    v-if="key !== optionalExtras.length - 1"
                  >, </span>

                </span>
              </p>
              <p
                v-else
                class="caption mb-0"
              >
                Any
              </p>
            </div>
            <v-divider />
          </v-expansion-panel>

          <!-- POLICY TYPE -->
          <v-expansion-panel id="policy-type">
            <v-expansion-panel-header><strong>Policy Type</strong></v-expansion-panel-header>
            <v-expansion-panel-content v-scroll-to-viewport>
              <p class="body-2 mb-0">
                Some providers offer a monthly rolling policy, which can be cancelled anytime.
              </p>
              <v-checkbox
                v-for="(type, index) in options.policyTypeOptions"
                :key="`policy-type-${index}`"
                v-model="policyTypes"
                :label="type.label"
                :value="type.value"
                hide-details
                dense
                class="mt-2 text-capitalize"
                @change="handlePolicyTypes(type.value)"
              />
            </v-expansion-panel-content>
            <div
              v-show="!panelsShown.includes(5)"
              class="v-expansion-panel-content__wrap"
            >
              <p class="caption mb-0 text-capitalize">
                {{ policyTypes && policyTypes.length > 0 ? policyTypes.join(', ') : 'None' }}
              </p>
            </div>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <!-- UPDATE QUOTE MODAL -->
    <v-card
      v-if="isUpdateQuoteShow"
      class="requote-message"
      elevation="2"
      light
    >
      <v-container
        fluid
        class="text-center d-flex flex-column justify-center align-center  pt-4"
      >
        <p
          class="mb-2 primary--text font-weight-medium"
        >
          Finished tailoring your policy?
        </p>
        <p
          class="body-2 mb-0 font-weight-medium"
        >
          When you’re done just click ‘Update Quotes’ to see your updated prices
        </p>
        <v-card-actions
          class="mt-6"
          style="width:100%;"
        >
          <v-btn
            x-large
            color="primary"
            rounded
            class="pl-4 pr-4"
            data-test="update_results_btn"
            @click="$emit('re-quote')"
          >
            <span>Update Quotes</span>
          </v-btn>
          <v-spacer />
          <v-btn
            x-large
            text
            rounded
            color="primary"
            data-test="cancel_btn"
            @click="$emit('cancel-re-quote', initialRequoteVal)"
          >
            <v-icon class="mr-1">
              $qz-close
            </v-icon>
            <span>Cancel</span>
          </v-btn>
        </v-card-actions>
      </v-container>
    </v-card>
  </div>
</template>

<script>
import {
  inject,
} from '@vue/composition-api';

export default {
  name: 'results-options',
  inject: ['constants'],
  props: {
    formData: {
      type: Object,
      required: false,
      default: () => {},
    },
    options: {
      type: Object,
      required: true,
    },
    selectedFilters: {
      type: Object,
      required: true,
    },
    detailsDescription: {
      type: Object,
      required: false,
      default: () => {},
    },
    isUpdateQuoteShow: {
      type: Boolean,
      default: false,
    },
    sidebarBreakpoint: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    panelsShown: [0, 4, 5],
    initialRequoteVal: {},
  }),
  mounted () {
    this.initialRequoteVal = {
      bicycleRacingCover: this.formData.cover.bicycleRacingCover,
      bicycleNonCompetitiveCover: this.formData.cover.bicycleNonCompetitiveCover,
    };
  },
  computed: {
    maxExcessText () {
      const currentOption = this.options.maxExcess.find(item => item.value === this.maxExcess);
      return currentOption.text;
    },
    bicycleRacingCover: {
      get () {
        return this.formData && this.formData.cover.bicycleRacingCover;
      },
      set (val) {
        this.internalTrackerResultsPage('racingCover', val);
        this.$emit('change-requote-filter', { filter: 'bicycleRacingCover', val });
      },
    },
    bicycleNonCompetitiveCover: {
      get () {
        return this.formData && this.formData.cover.bicycleNonCompetitiveCover;
      },
      set (val) {
        this.internalTrackerResultsPage('nonCompetitiveEventsCover', val);
        this.$emit('change-requote-filter', { filter: 'bicycleNonCompetitiveCover', val });
      },
    },
    maxExcess: {
      get () {
        return this.selectedFilters.maxExcess;
      },
      set (value) {
        this.internalTrackerResultsPage('maxExcess', value);
        this.$emit('change-filter', { filter: 'maxExcess', value });
      },
    },
    optionalExtras: {
      get () {
        return this.selectedFilters.optionalExtras;
      },
      set (value) {
        return this.$emit('change-filter', { filter: 'optionalExtras', value });
      },
    },
    policyTypes: {
      get () {
        return this.selectedFilters.policyTypes;
      },
      set (value) {
        return this.$emit('change-filter', { filter: 'policyTypes', value });
      },
    },
  },
  methods: {
    showQuoteDetails () {
      this.internalTrackerResultsPage('quoteSummary', true);
      this.$emit('click-details', ['details-quote-summary']);
    },
    showOptionalExtras () {
      this.$emit('click-details', ['details-description', 'optionalExtras']);
    },
    getOptionalExtrasTitle (key) {
      const optionalExtraTitle = this.detailsDescription.optionalExtras?.content.find((el) => el.slug === key);
      if (optionalExtraTitle) {
        return optionalExtraTitle.name;
      }
      return key;
    },
    getBooleanLabel (val) {
      return val ? 'Yes' : 'No';
    },
    onClickEditQuote () {
      this.internalTrackerResultsPage('editQuote', true);
      return this.$emit('click-edit-quote');
    },
    handleClickClose () {
      return this.$emit('click-close');
    },
    handleClickPaymentType (value) {
      this.internalTrackerResultsPage('paymentType', value ? 'Monthly' : 'Annually');
      return this.$emit('change-filter', { filter: 'isViewMonthly', value });
    },
    handleOptionalExtras (selectedItems, value) {
      this.internalTrackerResultsPage(this.constants.OPTIONAL_EXTRAS_TRACKER_OPTIONS[value], selectedItems.includes(value));
    },
    handlePolicyTypes (value) {
      this.internalTrackerResultsPage('montlhyPolicy', this.constants.POLICY_TYPE_TRACKER_OPTIONS[value]);
    },
  },
  setup () {
    const internalTrackers = inject('internalTrackers');

    return { ...internalTrackers };
  },
};
</script>

<style scoped lang="scss" src='@/assets/scss/results/results-options.scss'></style>
